import { Badge, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { PageHeader } from 'src/Components/Navigation/PageHeader';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import { AssetGovernanceFields, OwnershipNature, ShareType } from 'src/types/governance.types';
import { OWNERSHIP_MANTINE_COLOR } from 'src/Utils/ownershipColors';

import styles from './MyRightsSection.module.scss';

const NATURE_LABELS: Record<OwnershipNature, string> = {
  [OwnershipNature.PLEINE_PROPRIETE]: 'Pleine propriété',
  [OwnershipNature.USUFRUIT]: 'Usufruit',
  [OwnershipNature.NUE_PROPRIETE]: 'Nue-propriété',
};

const NATURE_DESCRIPTIONS: Record<OwnershipNature, string> = {
  [OwnershipNature.PLEINE_PROPRIETE]:
    'Vous pouvez occuper le bien, le louer et le vendre. Vous décidez de tout.',
  [OwnershipNature.USUFRUIT]:
    'Vous pouvez utiliser le bien et toucher les loyers, mais pas le vendre seul.',
  [OwnershipNature.NUE_PROPRIETE]:
    'Vous êtes propriétaire sur le papier, mais vous ne touchez rien tant que l’usufruit dure.',
};

const formatShares = (shares: number, shareType?: ShareType, totalShares?: number): string => {
  if (shareType === ShareType.PERCENTAGE) {
    return `${shares}%`;
  }
  if (totalShares && totalShares > 0) {
    const pct = Math.round((shares / totalShares) * 100);
    return `${shares} / ${totalShares} (${pct}%)`;
  }
  return `${shares}`;
};

const MyRightsSection = () => {
  const { t } = useTranslation();
  const { data: asset } = useFetchAssetDetails();

  const governance = asset as unknown as AssetGovernanceFields | undefined;
  const nature = governance?.currentUserOwnershipNature;

  if (!nature) return null;

  const shares = governance?.currentUserShares;

  return (
    <div>
      <PageHeader label={t('admin.myRights.title', 'Mes droits sur ce bien')} />
      <div className={styles.container}>
        <div className={styles.inputRow}>
          <span className={styles.inputLabel}>
            {t('admin.myRights.nature', 'Nature de propriété')}
          </span>
          <Badge color={OWNERSHIP_MANTINE_COLOR[nature]} variant="light" size="lg" radius="sm">
            {NATURE_LABELS[nature]}
          </Badge>
        </div>
        {shares != null && (
          <div className={styles.inputRow}>
            <span className={styles.inputLabel}>{t('admin.myRights.shares', 'Quote-part')}</span>
            <span>{formatShares(shares, governance?.shareType, governance?.totalShares)}</span>
          </div>
        )}
        <Text className={styles.description} size="sm">
          {NATURE_DESCRIPTIONS[nature]}
        </Text>
      </div>
    </div>
  );
};

export default MyRightsSection;
